import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence, useInView } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PlotCard from "@/components/PlotCard";
import EnquiryModal from "@/components/EnquiryModal";
import { FAQSection } from "@/components/FAQSection";
import { ReviewsSection } from "@/components/ReviewsSection";
import { SeoContentSections } from "@/components/SeoContentSections";
import { PageSEO } from "@/components/PageSEO";
import { useLang } from "@/context/LanguageContext";
import {
  CheckCircle, ArrowRight, Award, Users, Building2,
  ShieldCheck, Zap, TrendingUp, MapPin, ChevronLeft, ChevronRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const heroSlides = [
  {
    image: "https://static.prod-images.emergentagent.com/jobs/887722b5-7ad9-4cfc-8366-fffd7eed17f9/images/ab77224a3ec86682d71f9840eb537db3dcf3fc735d12a85599ad4cf1f5988938.jpeg",
  },
  {
    image: "https://static.prod-images.emergentagent.com/jobs/887722b5-7ad9-4cfc-8366-fffd7eed17f9/images/7dc77085bd950e780d35d5ac750f49fe919d743e47f4fcefd887e07f4e5cb099.jpeg",
  },
  {
    image: "https://static.prod-images.emergentagent.com/jobs/887722b5-7ad9-4cfc-8366-fffd7eed17f9/images/bf4390bb09d08526dd777e37685a9eacaa670853055d92f3cc5a0b3262face7a.jpeg",
  },
  {
    image: "https://static.prod-images.emergentagent.com/jobs/887722b5-7ad9-4cfc-8366-fffd7eed17f9/images/a31d2b3f6b1cb7255440086e3b828476059e068d6fff380dcfe63960cbcf7f93.jpeg",
  },
];

const featureIcons = [ShieldCheck, Zap, MapPin, CheckCircle, TrendingUp, Award, Building2, Users];

const SLIDE_DURATION = 5500;

/* ── Scroll-reveal wrapper ── */
function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ── Count-up number ── */
function CountUp({ target, suffix = "" }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const num = parseInt(target, 10);
    if (isNaN(num)) { setCount(target); return; }
    let start = 0;
    const step = Math.ceil(num / 40);
    const timer = setInterval(() => {
      start += step;
      if (start >= num) { setCount(num); clearInterval(timer); }
      else setCount(start);
    }, 30);
    return () => clearInterval(timer);
  }, [isInView, target]);

  return (
    <span ref={ref} className="stat-number" data-testid="stat-number">
      {typeof count === "number" ? count : target}{suffix}
    </span>
  );
}

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [plots, setPlots] = useState([]);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [selectedPlot, setSelectedPlot] = useState(null);
  const { t } = useLang();
  const slideTimer = useRef(null);
  const [progressKey, setProgressKey] = useState(0);

  const resetTimer = useCallback(() => {
    clearInterval(slideTimer.current);
    slideTimer.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      setProgressKey((k) => k + 1);
    }, SLIDE_DURATION);
  }, []);

  useEffect(() => {
    axios.get(`${API}/plots`).then((r) => {
      const pick = (sz) => r.data.find((p) => p.size === sz);
      const featured = [pick(1000), pick(2000)].filter(Boolean);
      setPlots(featured.length ? featured : r.data.slice(0, 2));
    }).catch(() => {});
    resetTimer();
    return () => clearInterval(slideTimer.current);
  }, [resetTimer]);

  const goSlide = (idx) => {
    setCurrentSlide(idx);
    setProgressKey((k) => k + 1);
    resetTimer();
  };

  const handleEnquire = (plot) => { setSelectedPlot(plot); setEnquiryOpen(true); };

  const slides = t("home.slides");
  const features = t("home.features");

  return (
    <div className="bg-[#0F172A]">
      <PageSEO page="home" />
      <Navbar />

      {/* ═══════ HERO ═══════ */}
      <section className="relative h-screen min-h-[600px] overflow-hidden" data-testid="hero-section">
        {/* Slide images with Ken Burns */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="absolute inset-0"
          >
            <motion.img
              src={heroSlides[currentSlide].image}
              alt={Array.isArray(slides) ? slides[currentSlide]?.heading : "Industrial Park"}
              className="w-full h-full object-cover"
              initial={{ scale: 1 }}
              animate={{ scale: 1.1 }}
              transition={{ duration: 8, ease: "linear" }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/60 to-transparent" />
            <div className="absolute inset-0 bg-slate-950/30" />
          </motion.div>
        </AnimatePresence>

        {/* Blueprint grid overlay */}
        <div className="absolute inset-0 blueprint-grid opacity-30 pointer-events-none" />

        {/* Content */}
        <div className="absolute inset-0 flex items-center z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-2xl">
              <AnimatePresence mode="wait">
                <motion.div key={currentSlide}>
                  <motion.span
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="overline inline-block mb-4 border border-orange-500/30 px-3 py-1.5"
                  >
                    {Array.isArray(slides) ? slides[currentSlide]?.tag : ""}
                  </motion.span>
                  <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="text-white mb-4"
                    style={{
                      fontFamily: "'Barlow Condensed', sans-serif",
                      fontWeight: 900,
                      fontSize: "clamp(2.5rem, 5.5vw, 5rem)",
                      lineHeight: 0.95,
                      textTransform: "uppercase",
                      letterSpacing: "-0.03em",
                    }}
                  >
                    {Array.isArray(slides) ? slides[currentSlide]?.heading : ""}
                  </motion.h1>
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="text-slate-300 text-base md:text-lg mb-8 leading-relaxed max-w-xl"
                  >
                    {Array.isArray(slides) ? slides[currentSlide]?.sub : ""}
                  </motion.p>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="flex flex-wrap gap-3"
                  >
                    <Link
                      to="/plots"
                      className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 font-bold uppercase tracking-wider text-sm transition-all duration-300 flex items-center gap-2 hover:shadow-[0_0_30px_rgba(249,115,22,0.4)]"
                      data-testid="hero-view-plots-btn"
                    >
                      {t("home.viewAllPlots")} <ArrowRight className="w-4 h-4" />
                    </Link>
                    <button
                      onClick={() => setEnquiryOpen(true)}
                      className="border border-white/30 text-white hover:bg-white/10 px-8 py-3 font-bold uppercase tracking-wider text-sm transition-all duration-300"
                      data-testid="hero-enquire-btn"
                    >
                      {t("home.enquireNow")}
                    </button>
                  </motion.div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Floating price badge */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="hidden lg:flex absolute right-8 top-1/2 -translate-y-1/2 z-10 flex-col items-end"
          data-testid="hero-price-badge"
        >
          <div className="bg-slate-950/70 backdrop-blur-md border border-orange-500/40 px-6 py-4 shadow-[0_0_40px_rgba(249,115,22,0.25)]">
            <div className="text-slate-400 uppercase" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", letterSpacing: "0.2em" }}>Flat Rate</div>
            <div className="text-orange-400 leading-none mt-1" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: "2.75rem" }}>
              ₹7,500<span className="text-white ml-1" style={{ fontSize: "0.9rem", fontWeight: 600 }}>/sq.mtr</span>
            </div>
            <div className="text-slate-400 uppercase mt-1.5" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", letterSpacing: "0.12em" }}>SIDA Approved · 90-Yr Lease</div>
          </div>
          <motion.div
            className="h-16 w-px bg-gradient-to-b from-orange-500/70 to-transparent mt-1 mr-6"
            animate={{ scaleY: [1, 0.35, 1], opacity: [0.85, 0.25, 0.85] }}
            transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
            style={{ transformOrigin: "top" }}
          />
        </motion.div>

        {/* Slide indicators + progress */}
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
            <div className="flex items-center gap-4">
              {heroSlides.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goSlide(idx)}
                  className="flex-1 group"
                  data-testid={`hero-slide-indicator-${idx}`}
                >
                  <div className="h-0.5 bg-white/20 overflow-hidden">
                    {idx === currentSlide && (
                      <div key={progressKey} className="slide-progress h-full" />
                    )}
                    {idx < currentSlide && <div className="h-full w-full bg-orange-500/60" />}
                  </div>
                  <span className="block mt-2 text-xs text-slate-400 group-hover:text-white transition-colors" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", letterSpacing: "0.1em" }}>
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Nav arrows */}
        <button
          className="absolute left-4 top-1/2 -translate-y-1/2 border border-white/20 hover:border-orange-500 hover:bg-orange-500/10 text-white p-3 transition-all duration-300 z-10"
          onClick={() => goSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length)}
          data-testid="hero-prev-btn"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          className="absolute right-4 top-1/2 -translate-y-1/2 border border-white/20 hover:border-orange-500 hover:bg-orange-500/10 text-white p-3 transition-all duration-300 z-10"
          onClick={() => goSlide((currentSlide + 1) % heroSlides.length)}
          data-testid="hero-next-btn"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </section>

      {/* ═══════ STATS BAR ═══════ */}
      <section className="relative border-y border-white/10 bg-slate-900/70 backdrop-blur-sm py-10" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { num: "50", suffix: "+", label: t("home.plotsSold") },
              { num: "10", suffix: "+", label: t("home.yearsExp") },
              { num: "200", suffix: "+", label: t("home.clientsServed") },
              { num: "100", suffix: "%", label: t("home.sidaApproved") },
            ].map((stat, i) => (
              <Reveal key={stat.label} delay={i * 0.1}>
                <div>
                  <CountUp target={stat.num} suffix={stat.suffix} />
                  <div className="text-slate-400 text-sm mt-2 tracking-wide">{stat.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ ABOUT ═══════ */}
      <section className="relative py-24 lg:py-32 overflow-hidden" data-testid="about-section">
        {/* Background watermark */}
        <div className="absolute top-1/2 -translate-y-1/2 right-0 text-white/[0.02] pointer-events-none select-none" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: "clamp(8rem, 15vw, 18rem)", lineHeight: 1, textTransform: "uppercase", writingMode: "vertical-rl" }}>
          SIIDCUL
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left: Text */}
            <div className="lg:col-span-6">
              <Reveal>
                <span className="overline">{t("home.aboutTag")}</span>
                <div className="section-divider-left mt-3"></div>
              </Reveal>
              <Reveal delay={0.1}>
                <h2
                  className="text-white mb-6"
                  style={{
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 800,
                    fontSize: "clamp(2rem, 3.5vw, 3rem)",
                    textTransform: "uppercase",
                    lineHeight: 1.05,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {t("home.aboutTitle")}
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="text-slate-400 leading-relaxed mb-4">{t("home.aboutP1")}</p>
              </Reveal>
              <Reveal delay={0.25}>
                <p className="text-slate-400 leading-relaxed mb-8">{t("home.aboutP2")}</p>
              </Reveal>
              <Reveal delay={0.3}>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/plots"
                    className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 font-bold uppercase tracking-wider text-sm transition-all duration-300 hover:shadow-[0_0_20px_rgba(249,115,22,0.3)]"
                  >
                    {t("home.explorePlots")}
                  </Link>
                  <Link
                    to="/schemes"
                    className="border border-white/20 text-white hover:border-orange-500 hover:text-orange-400 px-6 py-2.5 font-bold uppercase tracking-wider text-sm transition-all duration-300"
                  >
                    {t("home.viewSchemes")}
                  </Link>
                </div>
              </Reveal>
            </div>

            {/* Right: Asymmetric image grid */}
            <div className="lg:col-span-6">
              <Reveal delay={0.2}>
                <div className="relative">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-3">
                      <div className="overflow-hidden">
                        <img
                          src="https://static.prod-images.emergentagent.com/jobs/887722b5-7ad9-4cfc-8366-fffd7eed17f9/images/7dc77085bd950e780d35d5ac750f49fe919d743e47f4fcefd887e07f4e5cb099.jpeg"
                          alt="Modern warehouse & logistics facility at IIE Sigaddi"
                          className="w-full h-48 object-cover hover:scale-105 transition-transform duration-700"
                        />
                      </div>
                      <div className="overflow-hidden">
                        <img
                          src="https://static.prod-images.emergentagent.com/jobs/887722b5-7ad9-4cfc-8366-fffd7eed17f9/images/bf4390bb09d08526dd777e37685a9eacaa670853055d92f3cc5a0b3262face7a.jpeg"
                          alt="Manufacturing facility under construction"
                          className="w-full h-32 object-cover hover:scale-105 transition-transform duration-700"
                        />
                      </div>
                    </div>
                    <div className="pt-8">
                      <div className="overflow-hidden">
                        <img
                          src="https://static.prod-images.emergentagent.com/jobs/887722b5-7ad9-4cfc-8366-fffd7eed17f9/images/ab77224a3ec86682d71f9840eb537db3dcf3fc735d12a85599ad4cf1f5988938.jpeg"
                          alt="Aerial view of the SIIDCUL Kotdwar industrial park"
                          className="w-full h-64 object-cover hover:scale-105 transition-transform duration-700"
                        />
                      </div>
                    </div>
                  </div>
                  {/* Floating badge */}
                  <div className="absolute -bottom-4 -left-4 bg-orange-500 text-white p-5 shadow-[0_0_40px_rgba(249,115,22,0.3)]">
                    <div className="font-bold text-3xl" style={{ fontFamily: "'JetBrains Mono', monospace" }}>90</div>
                    <div className="text-xs font-medium uppercase tracking-wider">{t("home.leasePeriod")}</div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ FEATURES GRID ═══════ */}
      <section className="relative py-24 border-y border-white/10" data-testid="features-section">
        <div className="absolute inset-0 blueprint-grid opacity-20 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Reveal>
            <div className="mb-14">
              <span className="overline">{t("home.whyChoose")}</span>
              <div className="section-divider-left mt-3"></div>
              <h2
                className="text-white"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: "clamp(2rem, 3vw, 2.8rem)", textTransform: "uppercase", lineHeight: 1.1 }}
              >
                {t("home.keyFeatures")}
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.06]">
            {Array.isArray(features) && features.map((fText, i) => {
              const Icon = featureIcons[i] || CheckCircle;
              return (
                <Reveal key={i} delay={i * 0.06}>
                  <div className="feature-card p-6 h-full flex items-start gap-4 group" data-testid={`feature-card-${i}`}>
                    <div className="w-10 h-10 flex items-center justify-center bg-orange-500/10 border border-orange-500/30 flex-shrink-0 group-hover:bg-orange-500/20 transition-colors">
                      <Icon className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                    </div>
                    <span className="text-slate-300 text-sm font-medium leading-relaxed">{fText}</span>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════ AVAILABLE PLOTS ═══════ */}
      <section className="py-24" data-testid="plots-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-14">
            <Reveal>
              <div>
                <span className="overline">{t("home.availableNow")}</span>
                <div className="section-divider-left mt-3"></div>
                <h2
                  className="text-white"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: "clamp(2rem, 3vw, 2.8rem)", textTransform: "uppercase", lineHeight: 1.1 }}
                >
                  {t("home.plotsForSale")}
                </h2>
              </div>
            </Reveal>
            <Link
              to="/plots"
              className="hidden md:flex items-center gap-2 text-sm text-orange-500 font-semibold hover:text-orange-400 transition-colors"
            >
              {t("home.viewAllPlots")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
            {plots.map((plot, i) => (
              <Reveal key={plot.id} delay={i * 0.1}>
                <PlotCard plot={plot} onEnquire={handleEnquire} />
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="text-center mt-10">
              <Link
                to="/plots"
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 font-bold uppercase tracking-wider text-sm transition-all duration-300 hover:shadow-[0_0_30px_rgba(249,115,22,0.3)]"
                data-testid="home-view-all-plots-btn"
              >
                {t("home.viewAllPlots")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ CTA BANNER ═══════ */}
      <section
        className="relative py-24 overflow-hidden"
        data-testid="cta-section"
        style={{
          backgroundImage: `url(${heroSlides[3].image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-slate-950/80" />
        {/* Ghost watermark */}
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden"
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontWeight: 900,
            fontSize: "clamp(4rem, 12vw, 12rem)",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.04)",
            lineHeight: 1,
            letterSpacing: "-0.04em",
          }}
        >
          INVEST NOW
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Reveal>
            <h2
              className="text-white mb-4"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: "clamp(2rem, 4vw, 3.5rem)", textTransform: "uppercase", lineHeight: 1.05, letterSpacing: "-0.02em" }}
            >
              {t("home.readyToSetup")}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-slate-300 mb-10 text-base max-w-xl mx-auto leading-relaxed">{t("home.ctaDesc")}</p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => setEnquiryOpen(true)}
                className="bg-orange-500 hover:bg-orange-600 text-white px-10 py-3.5 font-bold uppercase tracking-wider text-sm transition-all duration-300 hover:shadow-[0_0_30px_rgba(249,115,22,0.5)]"
                data-testid="cta-enquire-btn"
              >
                {t("home.enquireFree")}
              </button>
              <a
                href="tel:+919258959058"
                className="border border-white/30 text-white hover:bg-white/10 px-10 py-3.5 font-bold uppercase tracking-wider text-sm transition-all duration-300"
              >
                {t("home.call")} +91-9258959058
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ REVIEWS ═══════ */}
      <ReviewsSection />

      <SeoContentSections />

      <FAQSection />

      <Footer />

      <EnquiryModal
        isOpen={enquiryOpen}
        onClose={() => setEnquiryOpen(false)}
        selectedPlot={selectedPlot}
      />
    </div>
  );
}
